/**
 * Sources — IA screen 4, owned by F01 composing M1.
 *
 * The first tenant-data screen. Its job beyond listing: make an unhealthy
 * connection impossible to miss, because a source in `auth_error` has silently
 * stopped syncing and every downstream finding is quietly going stale.
 */
import { useCallback, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Callout,
  Card,
  DefinitionList,
  EmptyState,
  HelpText,
  Skeleton,
  StateChip,
  Timestamp,
} from '@dedupr/ui';
import { profileDataset } from '@dedupr/profile';
import { openLocalStore, type StoredRecord } from '../local/store.js';
import { abandonRun, findResumable, resumeRun } from '../local/resume.js';
import type { MatchRecord, MatchResult } from '@dedupr/match-core';
import { LocalRun } from './LocalRun.js';
import type { Source } from '@dedupr/api-client';
import { SyncPanel } from './SyncPanel.js';
import './Sources.css';
import { Connect } from './Connect.js';
import { api, ErrorState, useProviderNames, useQuery } from '@dedupr/app-kit';

/* ⚠️ **The hardcoded label map is gone (ADR-083).** It was one of three copies, and it was the only
   exhaustiveness-checked one — typed `Record<NonNullable<Source['provider']>, string>` against the
   OpenAPI enum. That check is what a published manifest makes impossible to keep: a provider the
   contract cannot enumerate is one no map can be complete about. Names come from the registry, and an
   id with no name renders as itself rather than blank. */

/** What the user should *do*, per state — not a restatement of the state. */
const NEEDS_ACTION: Partial<Record<NonNullable<Source['state']>, string>> = {
  auth_error:
    'Access was revoked or expired, so this source has stopped syncing. Reconnect it — findings from it are going stale until you do.',
  degraded:
    'Syncing is completing only partially. Recent changes may be missing from findings.',
  paused: 'Syncing is paused, so nothing new is coming in from here.',
  disconnected: 'Disconnected. Staged data is retained for the documented window, then purged.',
};

function SourceRow({ source, providerNames }: { source: Source; providerNames: Record<string, string> }) {
  const attention = NEEDS_ACTION[source.state];
  /* The raw id is the fallback, deliberately: `salesforce` is legible, and a blank where a system's
     name belongs reads as a broken row. */
  const providerLabel = providerNames[source.provider] ?? source.provider;
  const fmt = new Intl.NumberFormat();

  return (
    <li className="web-sources__row">
      <Card
        tone={source.state === 'auth_error' ? 'danger' : source.state === 'degraded' ? 'warning' : 'default'}
        title={source.display_name ?? providerLabel}
        headingLevel={3}
        action={<StateChip family="source" value={source.state} />}
      >
        <DefinitionList
          items={[
            { term: 'Provider', value: providerLabel },
            {
              term: 'Records',
              value:
                source.record_count != null ? fmt.format(source.record_count) : 'Not yet counted',
            },
            {
              term: 'Last sync',
              value: source.last_sync_at ? (
                <Timestamp value={source.last_sync_at} />
              ) : (
                'Never'
              ),
            },
            ...(source.granted_scopes?.length
              ? [
                  {
                    term: 'Access',
                    // Read-only vs write matters enough to state plainly: FR-4.5
                    // grants read scopes first and asks for write per action type.
                    value: source.granted_scopes.some((s) => /write|modify/i.test(s))
                      ? 'Read and write'
                      : 'Read-only',
                    hint: `${source.granted_scopes.length} scope${source.granted_scopes.length === 1 ? '' : 's'} granted`,
                  },
                ]
              : []),
          ]}
        />
        {/* role="alert" only for the state that means data has stopped flowing;
            using it for every non-perfect state would train people to ignore it. */}
        {attention ? (
          <HelpText tone={source.state === 'auth_error' ? 'error' : 'default'} role={source.state === 'auth_error' ? 'alert' : undefined}>
            {attention}
          </HelpText>
        ) : null}
      </Card>
    </li>
  );
}

/**
 * The platform's own view of connected sources, from the `/v1/` contract.
 *
 * Separate from `Connect` because the two answer different questions: this one is "what is connected
 * and is it healthy", `Connect` is "add something". They are stacked on one screen because adding a
 * source and seeing your sources is one task.
 */
function ConnectedSourceList({ orgId }: { orgId: string | undefined }) {
  /* Same-origin here — this app's own Worker serves the relay. `settings.` passes the gateway origin. */
  const providerNames = useProviderNames();
  const query = useQuery(
    () =>
      orgId
        ? api.listSources({ org_id: orgId })
        : Promise.reject(new Error('No organisation in scope')),
    [orgId]
  );

  /* No org: nothing to list, and **not** a reason to hide the connect surface above — a file upload
     needs no organisation at all, which is the whole point of the local test drive. Rendering
     nothing here rather than a card that says "come back later". */
  if (!orgId) return null;

  if (query.status === 'loading') {
    return (
      <div className="web-sources" aria-busy="true">
        <Skeleton variant="rect" height="9rem" />
        <Skeleton variant="rect" height="9rem" />
      </div>
    );
  }

  if (query.status === 'error') {
    return <ErrorState code={query.errorCode} message={query.errorMessage} onRetry={query.reload} />;
  }

  const sources = query.data?.data ?? [];

  if (sources.length === 0) {
    /* No empty-state illustration: the connect surface directly above IS the call to action, and a
       second "connect a source" prompt below it would be the same instruction twice. */
    return null;
  }

  const unhealthy = sources.filter((s) => s.state === 'auth_error' || s.state === 'degraded');

  return (
    <div className="web-sources">
      {/* Surfaced above the list as well as on the row: on a long list the
          unhealthy source may be below the fold, and "why are my findings
          stale?" should be answerable without scrolling. */}
      {unhealthy.length > 0 ? (
        <Card
          tone="warning"
          // Both halves agree in number: "1 source needs attention",
          // "2 sources need attention". Pluralising the noun but not the verb is
          // the classic version of this bug.
          title={
            unhealthy.length === 1
              ? '1 source needs attention'
              : `${unhealthy.length} sources need attention`
          }
          headingLevel={2}
        >
          <p className="web-sources__summary">
            {unhealthy.map((s) => s.display_name ?? providerNames[s.provider] ?? s.provider).join(', ')} —
            findings built from {unhealthy.length === 1 ? 'it' : 'them'} may be out of date.
          </p>
        </Card>
      ) : null}

      <ul className="web-sources__list">
        {sources.map((s) => (
          <SourceRow key={s.id} source={s} providerNames={providerNames} />
        ))}
      </ul>

      {/* ⚠️ This said "connecting happens in account settings" and carried a "Not built yet" badge.
          Both were true when written and are now false — connecting happens on this screen. A stale
          "not built" label is worse than none: it tells someone not to try the thing that works. */}
      <HelpText>
        Disconnecting a source keeps its staged data for the documented retention window, then purges
        it. Nothing is deleted from your own system, ever.
      </HelpText>
    </div>
  );
}

/**
 * The Sources screen: add a source, then see the ones you have.
 *
 * One screen for both, because "connect a source" and "see my sources" are one task — routing between
 * them would put the thing a new visitor needs behind a navigation step they have no reason to take.
 */
/**
 * An interrupted run, and the way back to it — **ADR-067 (1)'s resume, IA 39's checkpointing half**.
 *
 * ⚠️ **Renders nothing in the common case**, like `LocalSourceList` above it. A notice that appears
 * only when there is something to say costs a person nothing when there is not.
 *
 * ⚠️ **It is FIRST on this screen when it appears**, above the intake. Somebody returning to a
 * half-finished run needs to be told before they are offered a fresh file — otherwise they upload the
 * same file again and end up with two copies of their records, which is the outcome this exists to
 * prevent.
 */
function ResumeNotice() {
  const [nonce, setNonce] = useState(0);
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<string | null>(null);

  const query = useQuery(async () => {
    const store = await openLocalStore();
    try {
      return await findResumable(store);
    } finally {
      store.close();
    }
  }, [nonce]);

  /* ⚠️ **The outcome outlives the thing that produced it — found in a browser.** A successful resume
     makes `findResumable` return null, which unmounted this component and took the confirmation with
     it: the run finished and the screen simply went quiet. Rendering the outcome on its own, after
     there is nothing left to resume, is what makes "it worked" readable. Exactly the defect the
     Administration screen already records for bulk triage's confirmation. */
  if (query.status === 'success' && !query.data && outcome) {
    return (
      <Callout tone="success" title="The interrupted run was finished">
        <p>{outcome}</p>
      </Callout>
    );
  }
  /* ⚠️ Silent while loading and silent on error. This is an *addition* to the screen, so a failed
     read must not replace the intake with an error — the visitor's real task is still available. */
  if (query.status !== 'success' || !query.data) return null;
  const { run, records, resumable, reason } = query.data;

  const resume = async () => {
    setBusy(true);
    setOutcome(null);
    try {
      const result = await resumeRun(run.id, (recs) => matchInWorker(recs));
      setOutcome(
        `Finished. ${result.pairs.toLocaleString()} possible duplicate ${result.pairs === 1 ? 'pair' : 'pairs'} across ${result.entities.toLocaleString()} ${result.entities === 1 ? 'group' : 'groups'}.`
      );
    } catch (error) {
      setOutcome(error instanceof Error ? error.message : 'The match could not be finished.');
    } finally {
      setBusy(false);
      setNonce((n) => n + 1);
    }
  };

  const abandon = async () => {
    setBusy(true);
    try {
      await abandonRun(run.id);
    } finally {
      setBusy(false);
      setNonce((n) => n + 1);
    }
  };

  return (
    <Callout tone={resumable ? 'warning' : 'info'} title="A run on this device was interrupted">
      {outcome ? <p>{outcome}</p> : null}
      {reason === 'ready' ? (
        <>
          <p>
            {records.toLocaleString()} record{records === 1 ? '' : 's'} are still here from a run that
            did not finish matching. Nothing was lost — the matching can pick up from what is already
            stored, so you do not need the file again.
          </p>
          <div className="web-sources__resume-actions">
            <Button onClick={() => void resume()} disabled={busy} loading={busy}>
              Finish matching
            </Button>
            <Button variant="ghost" onClick={() => void abandon()} disabled={busy}>
              Leave it
            </Button>
          </div>
        </>
      ) : reason === 'needs_the_file_again' ? (
        <>
          {/* ⚠️ Says what is needed rather than offering a button that would open a file picker under
              the word "resume". A control that does not do what it says is worse than no control. */}
          <p>
            A run stopped while the file was still being read, so there is nothing here to carry on
            from. Choose the file again below — nothing was kept from the interrupted attempt.
          </p>
          <Button variant="ghost" onClick={() => void abandon()} disabled={busy}>
            Clear it
          </Button>
        </>
      ) : (
        <>
          <p>
            A run did not finish, and the records it was matching are no longer on this device — most
            likely they were removed since. There is nothing to carry on from.
          </p>
          <Button variant="ghost" onClick={() => void abandon()} disabled={busy}>
            Clear it
          </Button>
        </>
      )}
    </Callout>
  );
}

/**
 * Run the engine in a Worker over an arbitrary record set.
 *
 * ⚠️ Its own Worker rather than `useLocalMatch`'s: that hook owns a file's whole journey (parse,
 * map, match, persist) and resuming enters it halfway. Sharing it would mean giving the hook a second
 * entry point whose earlier stages must be skipped — the shape that produces "which fields are set
 * on this state object" bugs. The Worker protocol is three messages; reusing *it* is the reuse worth
 * having.
 */
function matchInWorker(records: MatchRecord[]): Promise<MatchResult> {
  return new Promise((resolve, reject) => {
    const worker = new Worker(new URL('../local/matchWorker.ts', import.meta.url), { type: 'module' });
    const done = (fn: () => void) => {
      worker.terminate();
      fn();
    };
    worker.onmessage = (event: MessageEvent<{ type: string; result?: MatchResult; message?: string }>) => {
      const msg = event.data;
      if (msg.type === 'done' && msg.result) done(() => resolve(msg.result!));
      else if (msg.type === 'error') done(() => reject(new Error(msg.message ?? 'The match failed.')));
    };
    worker.onerror = (event) =>
      done(() => reject(new Error(event.message || 'The local match engine could not start.')));
    worker.postMessage({ type: 'run', records });
  });
}

export function Sources({ orgId }: { orgId: string | undefined }) {
  return (
    <>
      {/* ⚠️ **Z-2.** `ConnectedSourceList` returns null without an org, so in local mode this screen
          was the connect panel and nothing else — the files actually on the device, which ARE this
          mode's sources, were listed nowhere. The nav's sub-items promise "All sources · Source
          detail · Sync history · Data profile" and none of the four existed.

          ⚠️ **And it renders ABOVE the connect panel, not below it.** Found by screenshotting the
          working screen: the connect panel carries multi-step setup guidance for three providers, so
          it is several screens tall — which pushed a local user's own files far below the fold on the
          screen whose first job is to show them. `LocalSourceList` renders nothing when there are no
          files, so a first-time visitor still sees the connect panel first; someone with files sees
          their files first. The order follows who is asking. */}
      {/* ⚠️ **The file intake lives HERE now, not at `/local`** — owner decision, 2026-08-06: local
          mode has the same UI and UX as cloud. `LocalRun` was a route in the web nav labelled "Load a
          file", which made local a *different product* with a different navigation. Adding a file is
          the local expression of the same entry action a cloud client performs on this screen, so it
          belongs on this screen. `LocalRun` is unchanged as a component — only its placement moved,
          which is why its tests still describe what it does. */}
      {/* Before the intake: somebody returning to a half-finished run must be told before they are
          offered a fresh file, or they upload the same one again and end up with two copies. */}
      {orgId ? null : <ResumeNotice />}
      {orgId ? null : <LocalRun />}
      {orgId ? null : <LocalSourceList />}
      {/* ⚠️ **Between the file list and the connect panel, deliberately.** Pulling is what a
          connection is FOR, so the control belongs next to the records it produces rather than
          buried under three providers' worth of setup guidance — the same fold reasoning that put
          `LocalSourceList` above `Connect` in Z-2. */}
      <SyncPanel />
      {/* `sources-connect` lives on a wrapper rather than inside `Connect`, which is shared with the
         onboarding journey and should not carry this screen's nav ids. */}
      <div id="sources-connect" tabIndex={-1}>
        <Connect orgId={orgId} />
      </div>
      {orgId ? <ConnectedSourceList orgId={orgId} /> : null}
    </>
  );
}

/**
 * The files on this device — local mode's sources.
 *
 * One screen covering four of the five sub-nav promises: the list (**All sources**), an expandable
 * row (**Source detail**), what the ingest recorded (**Sync history**), and the field profile
 * (**Data profile**). Expandable rather than four routes, because a file has one page's worth of
 * facts and splitting them would put each behind a navigation step with nothing on the other side.
 */
function LocalSourceList() {
  const [open, setOpen] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  const query = useQuery(async () => {
    const store = await openLocalStore();
    try {
      const sources = await store.listSources();
      const records = await store.allRecords();
      const checkpoints = await Promise.all(
        sources.map(async (src) => [src.id, await store.readCheckpoint(src.id)] as const)
      );
      return { sources, records, checkpoints: new Map(checkpoints) };
    } finally {
      store.close();
    }
  }, [nonce]);

  const remove = useCallback(async (sourceId: string) => {
    const store = await openLocalStore();
    try {
      await store.removeSource(sourceId);
    } finally {
      store.close();
    }
    setNonce((n) => n + 1);
  }, []);

  if (query.status === 'loading') {
    return (
      <div className="web-sources" aria-busy="true">
        <Skeleton variant="rect" height="9rem" />
      </div>
    );
  }
  if (query.status === 'error') {
    return <ErrorState code={query.errorCode} message={query.errorMessage} onRetry={query.reload} />;
  }

  const { sources, records, checkpoints } = query.data ?? { sources: [], records: [], checkpoints: new Map() };

  /* Nothing loaded: the connect panel directly above IS the call to action, so a second prompt here
     would be the same instruction twice — the reasoning the hosted list already uses. */
  if (sources.length === 0) return null;

  return (
    <div className="web-sources">
      {/* Four of the sub-nav's five items name this one list: "All sources" is the list, and
          "Source detail", "Sync history" and "Data profile" are all inside the panel a row expands
          into. Sending them to the same place is truer than three routes with nothing behind two. */}
      <ul className="web-sources__list" id="sources-list" tabIndex={-1}>
        {sources.map((source) => {
          const mine = records.filter((r) => r.source_id === source.id);
          const isOpen = open === source.id;
          return (
            <li key={source.id} className="web-sources__row">
              <div className="web-sources__rowHead">
                <div>
                  <p className="web-sources__name">{source.display_name}</p>
                  <p className="web-sources__meta">
                    {/* ⚠️ Singular when there is one. This file already warns that pluralising the
                        noun but not the verb is "the classic version of this bug" a few lines up, and
                        the first version of this row rendered "1 records". */}
                    {new Intl.NumberFormat().format(mine.length)}{' '}
                    {mine.length === 1 ? 'record' : 'records'} · loaded{' '}
                    {new Date(source.ingested_at).toLocaleString()}
                  </p>
                </div>
                <div className="web-sources__rowActions">
                  <Button
                    variant="secondary"
                    onClick={() => setOpen(isOpen ? null : source.id)}
                    aria-expanded={isOpen}
                  >
                    {isOpen ? 'Hide detail' : 'Show detail'}
                  </Button>
                  {/* ⚠️ Says "Remove from this device", not "Delete". Nothing is deleted anywhere
                      else — the original file on their disk is untouched, and a word that implies
                      otherwise on a destructive-looking control is the wrong word. */}
                  <Button variant="secondary" onClick={() => void remove(source.id)}>
                    Remove from this device
                  </Button>
                </div>
              </div>

              {isOpen ? <LocalSourceDetail records={mine} checkpoint={checkpoints.get(source.id) ?? null} /> : null}
            </li>
          );
        })}
      </ul>

      <HelpText>
        Removing a file here removes it from this browser only. The file on your own computer is
        untouched, and nothing was ever uploaded.
      </HelpText>
    </div>
  );
}

/** One file's detail: what the ingest recorded, and how usable each matched field is. */
function LocalSourceDetail({
  records,
  checkpoint,
}: {
  records: readonly StoredRecord[];
  checkpoint: { written: number; done: boolean; highWatermark: string | null } | null;
}) {
  /* ⚠️ Profiled over the MATCHED fields, not the file's original columns. The store keeps the
     canonical shape (`full_name`/`email`/`company`/`phone`) rather than every column of the upload,
     so a full column profile is only available on the run screen where the parsed file is still in
     memory. Said out loud below rather than presented as a profile of the whole file. */
  const profile = useMemo(
    () =>
      profileDataset(
        records.map((r) => ({
          full_name: r.full_name ?? null,
          email: r.email ?? null,
          company: r.company ?? null,
          phone: r.phone ?? null,
        })),
        ['email', 'full_name', 'company', 'phone']
      ),
    [records]
  );

  return (
    <div className="web-sources__detail">
      <Card title="What was read" headingLevel={3}>
        <DefinitionList
          items={[
            { term: 'Records on this device', value: new Intl.NumberFormat().format(records.length) },
            {
              term: 'Ingest',
              /* `done` is the checkpoint's own flag. A partial ingest is a real state — the ceiling
                 truncates at 50,000 — and reporting it as complete would misstate the row count
                 every figure downstream is built on. */
              value: checkpoint ? (checkpoint.done ? 'Complete' : 'Stopped before the end') : 'No record kept',
              hint: checkpoint
                ? `${new Intl.NumberFormat().format(checkpoint.written)} written${
                    checkpoint.highWatermark ? `, newest change seen ${checkpoint.highWatermark}` : ''
                  }`
                : 'This file was loaded before ingest progress was recorded.',
            },
          ]}
        />
      </Card>

      <Card title="How usable each matched field is" headingLevel={3}>
        <table className="web-sources__profile">
          <thead>
            <tr>
              <th scope="col">Field</th>
              <th scope="col">Has a value</th>
              <th scope="col">Distinct</th>
              <th scope="col">Looks like</th>
            </tr>
          </thead>
          <tbody>
            {profile.fields.map((f) => (
              <tr key={f.field}>
                <th scope="row">{f.field.replace('_', ' ')}</th>
                <td>{Math.round(f.completeness * 100)}%</td>
                {/* Distinctness, not the raw count: a column where 900 of 1000 rows say the same
                    thing is worse than useless as an identifier, and completeness alone would call
                    it excellent. */}
                <td>{Math.round(f.distinctness * 100)}%</td>
                <td>{f.format?.kind ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <HelpText>
          These are the four fields Dedupr matches on, as stored on this device. The full
          column-by-column profile of your original file is on the screen where you loaded it.
        </HelpText>
      </Card>
    </div>
  );
}
